import React, { useEffect, useMemo, useState } from "react";

import { LazyQueryExecFunction } from "@apollo/client";
import { AnyFilterExpressionInput, Attribute, ByProjectIdReqInput, Exact, MdmDeviceDto, MdmObjectDto, SortDirection, SortInput, useUpdateUserSettingsMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import useProjectContext from "../hooks/useProjectContext";
import { DEVICES_TABLE_NAME, OBJECTS_TABLE_NAME } from "../utils/const";
import { LazyState, TableColumn } from "../utils/types";
import { errorToaster, successToaster } from "../utils/utils";
import LazyTable from "./LazyTable";
import TableColumnSelection from "./TableColumnSelection";

type CustomFieldType = Record<
  string,
  {
    value: string | null | undefined;
    id: string | null | undefined;
    title: string | undefined;
  }
>;

type ObjectAndDeviceTableWrapperProps<T, N> = {
  data: T[];
  tableName: string;
  containerName: string;
  columns: TableColumn<T>[];
  loading: boolean;
  onLazyEvent: React.Dispatch<React.SetStateAction<LazyState>>;
  lazyState: LazyState;
  totalItems: number;
  getQueryData: LazyQueryExecFunction<N, Exact<{ input: ByProjectIdReqInput }>>;
};

const ObjectAndDeviceTableWrapper = <T extends NoInfer<MdmDeviceDto | MdmObjectDto>, N>({ 
  data,
  tableName,
  containerName,
  columns,
  loading,
  onLazyEvent,
  lazyState,
  totalItems,
  getQueryData,
 }: ObjectAndDeviceTableWrapperProps<T, N>) => {
  const [customColumns, setCustomColumns] = useState<string[]>([]);
  const { user, userSettings, project, projectId } = useProjectContext();
  const navigate = useNavigate();

  const [updateUserSettings, { loading: updateLoading }] = useUpdateUserSettingsMutation({
    onCompleted: () => successToaster("Columns saved successfully"),
    onError: errorToaster,
  });

  let columnList: string[] = [];
  let customCol: Attribute[] = [];

  if (tableName === DEVICES_TABLE_NAME) {
    columnList = (userSettings?.userSettings.deviceTable as string[]) ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? []),
    ] as Attribute[];
  } else if (tableName === OBJECTS_TABLE_NAME) {
    columnList = (userSettings?.userSettings.objectTable as string[]) ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.objectConfiguration.customFields ?? []),
    ] as Attribute[];
  }

  useEffect(() => {
    if (userSettings && columnList.length > 0) {
      setCustomColumns(columnList);
    } else {
      setCustomColumns(columns.map(col => col.header));
    }
  }, [userSettings]);

  const mapCol = useMemo(
    () =>
      [
        ...columns,
        ...(customCol?.map(col => ({
          field: `${col?.id}.value`,
          header: col?.title ?? "",
          sortable: true,
          columnKey: col?.id,
        })) ?? []),
      ] as TableColumn<T>[],
    [columns]
  );

  const formatObjectFields = data.reduce((acc, obj) => {
    const objFields = obj.customFields?.reduce((accObj, field) => {
      const newObj = {
        value: field?.value?.value,
        id: field?.key,
        title: customCol.find(col => col?.id === field?.key)?.title,
      };
      return {
        ...accObj,
        [field?.key!]: newObj,
      };
    }, {} as CustomFieldType);

    acc.push({
      ...obj,
      ...objFields,
    });

    return acc;
  }, [] as Partial<T>[]);

  const filteredColumns = useMemo(
    () => mapCol.filter(col => customColumns.includes(col.header)),
    [customColumns, mapCol]
  );

  const handleColumnSave = () => {
    if (tableName === DEVICES_TABLE_NAME) {
      updateUserSettings({
        variables: {
          userSettings: {
            id: user?.externalId!,
            deviceTable: customColumns,
          },
        },
      });
      return;
    }
    updateUserSettings({
      variables: {
        userSettings: {
          id: user?.externalId!,
          objectTable: customColumns,
        },
      },
    });
  };

  const fetchData = (
    take: number | null = null,
    skip: number | null = null,
    sort: SortInput[] | null = null,
    filters: AnyFilterExpressionInput[] | null = null,
    cacheAndNetwork: boolean = false,
  ) => {
    getQueryData({
      variables: {
        input: {
          projectId,
          take: take ?? lazyState.rows,
          skip: skip ?? lazyState.first,
          sort: sort ?? [{
            property: lazyState.sortField,
            direction: lazyState.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
          }],
          andFilters: filters ?? lazyState.andFilters,
        },
      },
      fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
    });
  };

  return (
    <LazyTable
      columns={filteredColumns}
      data={formatObjectFields}
      tableName={tableName}
      containerName={containerName}
      loading={loading}
      onLazyEvent={onLazyEvent}
      lazyState={lazyState}
      totalItems={totalItems}
      fetchData={fetchData}
      buttons={
        <>
          <TableColumnSelection
            customColumns={customColumns}
            setCustomColumns={setCustomColumns}
            handleColumnSave={handleColumnSave}
            mapCol={mapCol}
            loading={updateLoading}
          />
          <Button
            type="button"
            icon="pi pi-plus"
            label={tableName === DEVICES_TABLE_NAME ? "Add Device" : "Add Object"}
            onClick={() => navigate(tableName === DEVICES_TABLE_NAME ? "new" : "objects/new")}
          />
        </>
      }
    />
  );
}

export default ObjectAndDeviceTableWrapper;